import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { type Theme, useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PREDEFINED_CLAIM_FILTERS } from "../../../../status";
import useDevMode from "../../../../utils/devmode";
import ClaimsTable from "../../../ClaimsTable";
import Loading from "../../../Loading";
import { usePatientInsurance } from "../../hooks";
import { InsurancePolicyTab } from "./InsurancePolicyTab";
import { PatientInfoTab } from "./PatientInfo";
import PatientProfileHeader from "./PatientProfileHeader";
import RelatedProfiles from "./RelatedProfiles";

export enum PatientProfileTabs {
  PatientInfo,
  InsurancePolicy,
  InsurancePayments,
}

export interface PayerFilter {
  payerId: string;
  payerName: string | null;
}

function renderHeader({
  isLoading,
  theme,
  patient,
  onBack,
  openPatientEditModal,
}: {
  isLoading: boolean;
  theme: Theme;
  patient: PatientMessage | undefined;
  onBack: () => void;
  openPatientEditModal: () => void;
}) {
  if (isLoading) {
    return <Box sx={{ height: "5.5625rem" }} />;
  }

  return (
    <Box sx={{ marginBottom: theme.spacing(2) }}>
      <PatientProfileHeader
        onClickBackButton={onBack}
        openPatientEditModal={openPatientEditModal}
        patient={patient}
      />
    </Box>
  );
}

function renderPatientInfo({
  isLoading,
  patient,
  editPatient,
  updateSnackBar,
  closePatientEditModal,
  triggerPatientEditModal,
  practices,
  relatedProfiles,
}: {
  isLoading: boolean;
  patient: PatientMessage | undefined;
  editPatient: (patient: PatientMessage) => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  closePatientEditModal: () => void;
  triggerPatientEditModal: boolean;
  practices: PracticeMessage[];
  relatedProfiles?: React.ReactNode;
}) {
  if (isLoading) {
    return (
      <Box sx={{ height: "12rem" }}>
        <Loading />
      </Box>
    );
  }

  return (
    <PatientInfoTab
      patient={patient}
      onSave={editPatient}
      updateSnackBar={updateSnackBar}
      triggerPatientEditModal={triggerPatientEditModal}
      closePatientEditModal={closePatientEditModal}
      practices={practices}
      relatedProfiles={relatedProfiles}
    />
  );
}

function renderInsurancePayments({
  isLoading,
  claims,
  update,
  saveClaim,
  toPMS,
  practices,
  supportedSources,
  onDone,
  payerFilter,
  onClearPayerFilter,
  excludeClaimColumns,
  onDenialUpdate,
}: {
  isLoading: boolean;
  claims: ClaimWithProcedureAndPatientMessage[];
  update: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  saveClaim: (claim: ClaimMessage) => Promise<void>;
  toPMS: (claim: ClaimWithProcedureAndPatientMessage) => void;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  onDone: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  payerFilter: PayerFilter | null;
  onClearPayerFilter: () => void;
  excludeClaimColumns: string[];
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => Promise<void>;
}) {
  return (
    <>
      {payerFilter && (
        <Box sx={{ marginBottom: "16px" }}>
          <Chip
            label={`Payer: ${payerFilter.payerName ?? "Unknown"}`}
            onDelete={onClearPayerFilter}
          />
        </Box>
      )}
      <ClaimsTable
        payment={null}
        practices={practices}
        supportedSources={supportedSources}
        claims={claims}
        isLoading={isLoading}
        onClaimUpdate={update}
        onSaveClaim={saveClaim}
        allowAddClaim={false}
        enableClaimDetails
        onPostToPMS={toPMS}
        onDone={onDone}
        onDenialUpdate={onDenialUpdate}
        currentTab={PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS}
        excludeByFieldNames={excludeClaimColumns}
        initialSortingModel={[{ field: "availableSince", sort: "desc" }]}
        sortColumnOrder={[
          "claimStatus",
          "practiceDisplayName",
          "payer",
          "wieldyPatientName",
          "claimId",
          "dateOfService",
          "claimPayerPays",
          "postedState",
          "postedDateTime",
          "denialStatus",
          "action",
          "denialUrgency",
          "doneButton",
          "takeActionButton",
          "daysSinceNotice",
          "daysSinceService",
          "documents",
        ]}
        pagination={{
          page: 1,
          pageSize: 500,
          rowCount: claims.length,
          totalPages: 1,
          onPageChange: () => undefined,
          onPageSizeChange: () => undefined,
        }}
      />
    </>
  );
}

interface PatientProfileViewProps {
  patient?: PatientMessage;
  patientLoading: boolean;
  claims: ClaimWithProcedureAndPatientMessage[];
  claimsLoading: boolean;
  update: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  saveClaim: (claim: ClaimMessage) => Promise<void>;
  editPatient: (patient: PatientMessage) => void;
  toPMS: (claim: ClaimWithProcedureAndPatientMessage) => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  onDone: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => Promise<void>;
  // Supplied when rendered outside the route (e.g. the Insurance A/R modal).
  patientId?: string;
  onBack?: () => void;
  initialTab?: PatientProfileTabs;
  payerFilter?: PayerFilter | null;
  onClearPayerFilter?: () => void;
  excludeClaimColumns?: string[];
}
const NO_INSURANCE: PatientInsuranceMessage[] = [];

export default function PatientProfileView({
  patient,
  patientLoading,
  claims,
  claimsLoading,
  update,
  saveClaim,
  editPatient,
  toPMS,
  updateSnackBar,
  practices,
  supportedSources,
  onDone,
  onDenialUpdate,
  patientId: patientIdProp,
  initialTab,
  payerFilter,
  onClearPayerFilter,
  excludeClaimColumns,
  onBack,
}: PatientProfileViewProps) {
  const { patientId: routePatientId } = useParams();
  const patientId = patientIdProp ?? routePatientId;
  const theme = useTheme();
  const navigate = useNavigate();
  const devMode = useDevMode();

  const hasRelatedProfiles =
    devMode && patient?.profiles && patient.profiles.length > 1;

  const [selectedTab, setSelectedTab] = useState(
    initialTab ?? PatientProfileTabs.PatientInfo,
  );

  const [triggerPatientEditModal, setTriggerPatientEditModal] = useState(false);

  const insuranceQuery = usePatientInsurance(
    patientId,
    selectedTab === PatientProfileTabs.InsurancePolicy,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar would re-fire the snackbar
  useEffect(() => {
    if (insuranceQuery.isError) {
      updateSnackBar({
        severity: "error",
        message: "Failed to load insurance data",
      });
    }
  }, [insuranceQuery.isError]);

  const openPatientEditModal = () => {
    setTriggerPatientEditModal(true);
  };

  const closePatientEditModal = () => {
    setTriggerPatientEditModal(false);
  };

  return (
    <Box
      sx={{
        paddingTop: theme.spacing(4),
      }}
    >
      {renderHeader({
        isLoading: patientLoading,
        theme,
        patient,
        onBack: onBack ?? (() => navigate(-1)),
        openPatientEditModal,
      })}
      <Tabs
        value={selectedTab}
        onChange={(_event, value) => setSelectedTab(value)}
        sx={{
          marginBottom: theme.spacing(2),
          minHeight: "42px",
          height: "auto",
        }}
        variant="fullWidth"
      >
        <Tab
          data-testid="patientInfo"
          label="Patient Info"
          value={PatientProfileTabs.PatientInfo}
          sx={{ minHeight: "auto" }}
        />
        {devMode && (
          <Tab
            data-testid="insurancePolicy"
            label="Insurance Policies"
            value={PatientProfileTabs.InsurancePolicy}
            sx={{ minHeight: "auto" }}
          />
        )}
        <Tab
          data-testid="insurancePayments"
          label="Insurance Payments"
          value={PatientProfileTabs.InsurancePayments}
          sx={{ minHeight: "auto" }}
        />
      </Tabs>
      {selectedTab === PatientProfileTabs.InsurancePayments &&
        renderInsurancePayments({
          isLoading: claimsLoading,
          claims,
          update,
          saveClaim,
          toPMS,
          practices,
          supportedSources,
          onDone,
          payerFilter: payerFilter ?? null,
          onClearPayerFilter: onClearPayerFilter ?? (() => undefined),
          excludeClaimColumns: excludeClaimColumns ?? [],
          onDenialUpdate,
        })}
      {selectedTab === PatientProfileTabs.PatientInfo &&
        renderPatientInfo({
          isLoading: patientLoading,
          patient,
          editPatient,
          updateSnackBar,
          closePatientEditModal,
          triggerPatientEditModal,
          practices,
          relatedProfiles: hasRelatedProfiles ? (
            <RelatedProfiles
              patient={patient}
              currentPatientId={patientId || ""}
            />
          ) : undefined,
        })}
      {selectedTab === PatientProfileTabs.InsurancePolicy && patientId && (
        <InsurancePolicyTab
          isLoading={insuranceQuery.isPending}
          insuranceData={insuranceQuery.data ?? NO_INSURANCE}
          patientId={patientId}
        />
      )}
    </Box>
  );
}

PatientProfileView.defaultProps = {
  patient: undefined,
};
